import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Input, Row, Switch, Spin } from 'antd';
import cn from 'classnames';
import {
    IAddress,
    ICity,
    ICountryOrState,
    IListItem,
    IUserAddressRulesData,
    IUserAddressRulesField
} from 'base/interfaces/address';
import {
    addAddress,
    getCityByState,
    getCountryAndState,
    getUserAddressRules,
    updateAddress
} from 'base/services/address';
import { mapCountrisAndStates } from 'base/utils/address';
import { useStore, useStoreState } from 'base/store';
import FormEmail from '@base/snippets/FormEmail';
import Message from '@base/lib/Message';
import {
    getTargetCountryInfoWithName, focusErrInfoFormItem
} from '@base/account/address/utils';
import switchStyles from 'app/styles/modules/switch-style.less';
import styles from './index.module.less';
import { useTrans } from 'base/utils/i18n';
import useAriaLive from 'base/hooks/useAriaLive';
import CountryFormItem from './Fields/CountryFormItem';
import ProvinceFormItem from './Fields/ProvinceFormItem';
import CityFormItem from './Fields/CityFormItem';
import ZipcodeFormItem from './Fields/ZipcodeFormItem';
import MobileFormItem from './Fields/MobileFormItem';
import FormInput from './Fields/FormInput';
import { mockAddressRules } from './mock';

export type AddressFormProps = {
    formRef?: (ref: any) => void;
    address?: IAddress;
    onCancel?: () => void;
    isSetDefault?: boolean;
    isSubmitDisabled?: boolean;         // 设置为默认地址
    isDisabledSelectCountry?: boolean;  // 是否默认当前国家，不能选择国家
    isOnChangeValidate?: boolean;       // FormItem onChange时是否验证rules (true:  validateTrigger默认值）
    showCancelButton?: boolean;
    showDefaultSwitch?: boolean;
    showShippingNote?: boolean;
    showSubmit?: boolean;
    className?: string;
    counter?: number; // 强制给表单赋值
    onSubmit?: (address: IAddress) => Promise<void>;
    onSubmitSelf?: (data: IAddress) => Promise<void>; // 自己提交 不走默认的接口
    hideEmail?: boolean;
    onValuesChange?: (changeValues: any, allValues: IAddress) => void;
};

type IColItem = {
    itemRules: IUserAddressRulesField;
    children: React.ReactNode;
}

const ColItem: React.FC<IColItem> = (props) => {
    const {
        itemRules,
        children,
    } = props;
    return (
        <Col
            span={itemRules.colSpan || 24}
            key={itemRules.key}
        >
            {children}
        </Col>
    )
}


const AddressForm: React.FC<AddressFormProps> = props => {
    const {
        formRef,
        address,
        onSubmit,
        onCancel,
        onSubmitSelf,
        isSetDefault = false,
        isSubmitDisabled = false,
        isDisabledSelectCountry = false,
        isOnChangeValidate: isOnChangeValidateDefault = false,
        showCancelButton = true,
        showDefaultSwitch = false,
        showShippingNote = false,
        showSubmit = true,
        className,
        counter,
        hideEmail = false,
        onValuesChange
    } = props;

    const ariaLive = useAriaLive({
        id: 'address-form-aria',
        options: { ariaLive: 'assertive' }
    })
    const { onSetAriaLiveContent } = ariaLive

    const [form] = Form.useForm();
    const { t } = useTrans();

    const [initIng, setInitIng] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)
    const [addressRules, setAddressRules] = useState<IUserAddressRulesField[]>()
    const [countries, setCountries] = useState<ICountryOrState[]>([]);
    const [countriesMap, setCountriesMap] = useState<Record<number | string, ICountryOrState>>();
    // 表单国家等options数据
    const [currentCountryObj, setCurrentCountryObj] = useState<ICountryOrState>()
    // 网站当前选中的国家数据
    const [webCurrentCountry, setWebCurrentCountry] = useState<ICountryOrState>()
    const [cityList, setCityList] = useState<IListItem[]>([])
    const [cityLoading, setCityLoading] = useState<boolean>(false)
    const [isOnChangeValidate, setIsOnChangeValidate] = useState<boolean>(isOnChangeValidateDefault)
    const [zipCodeList, setZipCodeList] = useState<string[]>([])

    const isValidateAll = isOnChangeValidate || isOnChangeValidateDefault
    const { userStore } = useStore();
    const isDealer = userStore.state.userTags.includes('DEALER');
    const { country: currentCountryCode } = useStoreState('rootStore')

    // const zipSelectRef = useRef<HTMLElement>(null)

    // 通过name 获取对应数据
    const getTargetCountryInfo = (
        name: string,
        listMap?: Record<string | number, ICountryOrState>
    ): ICountryOrState | undefined => {
        const _countriesMap = (listMap ? listMap : countriesMap) || countriesMap
        if (!_countriesMap) {
            return void 0
        }
        return getTargetCountryInfoWithName(name, _countriesMap)
    }

    // 格式化表单值（返回不在表单中但需要的值）
    const formatFormValuesHandle = (values: any): IAddress => {
        const { countryText, provinceText, isDefault = 0 } = values;

        const _country = getTargetCountryInfo(countryText)
        const _province = getTargetCountryInfo(provinceText)
        // cityId miss toDo
        return {
            ...values,
            isDefault: isSetDefault ? 1 : (isDefault ? 1 : 0),
            countryId: values.countryId || _country?.id || '',
            provinceId: values.provinceId || _province?.id || '',
        }
    }

    // 重置表单
    const resetFormHandle = () => {
        if (address) {
            form.setFieldsValue(address);
            if (countriesMap) {
                defaultCityZipCodeValuesSet(address.countryText, countriesMap).then()
            }
        } else {
            form.resetFields();
            if (countries.length > 0) {
                const _country = webCurrentCountry || countries[0]
                form.setFieldsValue({
                    countryId: _country.id,
                    countryText: _country.name
                })
            }
            form.setFieldsValue({
                isDefault: 0
            })
        }
    };

    const onFinishFailed = async (s: any) => {
        const { errorFields } = s
        if (!!errorFields.length) {
            const firstErr = errorFields[0]
            focusErrInfoFormItem({ errorFields })
            await onSetAriaLiveContent(firstErr.errors[0], 2000)
        }
    }
    // 提交表单
    const submitHandle = async (values: IAddress) => {
        try {
            setLoading(true)
            setIsOnChangeValidate(true)     // 一次提交后设置 onChange rules验证

            let _values: any = {}
            // 值 trim()
            Object.keys(values).map((key) => {
                const _v = values[key as keyof IAddress]
                _values[key] = (_v && typeof _v == 'string') ? (_v + '')?.trim() : _v
            })

            form.setFieldsValue(_values)
            // 再验证表单
            try {
                await form.validateFields()
            } catch (e) {
                onFinishFailed(e).then()
                return
            }

            const { countryText, provinceText, isDefault = 0 } = _values;

            const _country = getTargetCountryInfo(countryText)
            const _province = getTargetCountryInfo(provinceText)

            const params: IAddress = {
                ..._values,
                isDefault: isSetDefault ? 1 : (isDefault ? 1 : 0),
                countryId: _country?.id || '',
                provinceId: _province?.id || '',
            }

            if (typeof onSubmitSelf === 'function') {
                await onSubmitSelf?.(params)

            } else {
                // 更新 添加地址
                if (address) {
                    await updateAddress({
                        ...address,
                        ...params
                    });
                } else {
                    await addAddress(params);
                }
                typeof onSubmit === 'function' && await onSubmit?.(params);
            }
        } catch (e) {
            Message.error(e.message || e?.errorFields?.[0]?.errors?.[0]);
        } finally {
            setLoading(false)
        }
    }

    // 获取城市列表
    const getCityList = async (e: string, targetObj?: ICountryOrState): Promise<(ICity[] | undefined)> => {
        const _countryObj = targetObj || currentCountryObj
        const province = (_countryObj?.children as ICountryOrState[])?.find(pItem => {
            return pItem.name == e
        })

        if (!province) {
            return
        }
        try {
            setCityLoading(true)
            const cityList = await getCityByState({
                stateId: province.id
            })
            setCityList(cityList)
            return cityList as ICity[]
        } catch (e) {
            Message.error(e.message)
            return void 0
        } finally {
            setCityLoading(false)
        }
    }

    // 国家修改规则修改
    const changeRulesHandle = (formRulesRes: IUserAddressRulesData) => {
        setAddressRules(formRulesRes.fields || mockAddressRules.fields);
    }

    // 表单值onChange修改
    const onValuesChangeHandle = (changeValues: any, allValues: any) => {
        const newValues = formatFormValuesHandle(allValues)
        onValuesChange?.(changeValues, newValues)
    }

    // country国家选择
    const onSelectCountryHandle = async (countryText: string) => {
        try {
            setLoading(true);
            const countryObj = countryText ? getTargetCountryInfo(countryText as string) : void 0

            const formRulesRes = await getUserAddressRules({
                countryId: countryObj?.id
            })

            changeRulesHandle(formRulesRes)
            setCurrentCountryObj(countryObj)
            setCityList([])
            setZipCodeList([])
            form.resetFields()
            form.setFieldsValue({
                ...(address ? {
                    id: address.id,
                    isDefault: address.isDefault,
                } : {}),
                countryText: countryText,
                countryId: countryObj?.id,
            })

        } catch (err) {
        } finally {
            setLoading(false)
        }

    }
    // province省份选择
    const onSelectProvinceHandle = (provinceText: string) => {
        getCityList(provinceText as string).then()
        const _province = (currentCountryObj?.children as ICountryOrState[])?.find(item => item.name == provinceText)
        setZipCodeList([])
        form.setFieldsValue({
            provinceText: provinceText,
            provinceId: _province?.id,
            cityText: void 0,
            cityId: void 0,
            zipcode: void 0,
        })
        const allValues = form.getFieldsValue()
        onValuesChangeHandle({ provinceText }, allValues)
    }
    // city城市选择
    const onSelectCityHandle = (cityText: string) => {
        const currentCity = cityList.find(cItem => cItem.name == cityText)
        if (currentCity) {
            const _currentCity = currentCity as ICity
            setZipCodeList(_currentCity.zipCodeList)
            const zipCodeList = _currentCity.zipCodeList
            form.setFieldsValue({
                cityText: cityText,
                cityId: _currentCity.id,
                zipcode: zipCodeList?.length > 1 ? '' : _currentCity.zipCode,
            })

            if (zipCodeList?.length > 1) {
                // 自动focus
                document?.getElementById('zipcode')?.focus()
            }

        }
        const allValues = form.getFieldsValue()
        onValuesChangeHandle({ cityText }, allValues)
    }

    // 当前国家对应默认值设置
    const defaultCityZipCodeValuesSet = async (selectedCountryText: string | undefined, countriesMapValues: any) => {
        // 当前国家数据
        const countryObj = selectedCountryText ? getTargetCountryInfo(selectedCountryText, countriesMapValues) : void 0
        setCurrentCountryObj(countryObj)

        if (countryObj && address?.provinceText) {
            const _cityList = await getCityList(address.provinceText, countryObj)

            if (address.zipcode) {
                const _zip = _cityList?.find((item) => item?.id == address.cityId)
                setZipCodeList(_zip?.zipCodeList || [])
            } else {
                setZipCodeList([])

            }
        } else {
            setCityList([])
            setZipCodeList([])
        }
    }

    // 取消
    const onCancelHandle = () => {
        form.resetFields()
        onCancel?.()
    }

    // 初始化
    const initHandle = async () => {
        try {
            setInitIng(true)
            // 获取国家省份列表
            const [formRulesRes, countryAndStateRes] = await Promise.all([
                getUserAddressRules({
                    ...(address?.countryId ? { countryId: address?.countryId || '' } : {})
                }),
                getCountryAndState()
            ])

            changeRulesHandle(formRulesRes);
            setCountries(countryAndStateRes);

            const newCountriesMap = mapCountrisAndStates(countryAndStateRes)
            setCountriesMap(newCountriesMap);
            if (countryAndStateRes.length > 0) {
                let _countryText = address?.countryText
                // 匹配当前网站选中的国家
                const _currentCountry = countryAndStateRes.find(item => item.code == currentCountryCode)
                setWebCurrentCountry(_currentCountry)

                if (!address) {
                    // 表单国家默认值
                    const _country = _currentCountry || countryAndStateRes[0]
                    form.setFieldsValue({
                        countryId: _country.id,
                        countryText: _country.name
                    })
                    _countryText = _country.name
                }

                defaultCityZipCodeValuesSet(_countryText, newCountriesMap).then()
            }
        } catch (err) {
            Message.error(err.message);
        } finally {
            setInitIng(false)
        }
    }


    useEffect(() => {
        initHandle().then()
    }, []);

    // 表单赋值
    useEffect(() => {
        resetFormHandle();
    }, [address, counter]);

    // form实例对象上传父组件
    useEffect(() => {
        if (form) {
            formRef?.(form)
        }
    }, [form])

    const validateMessages = {
        required: '${label} ' + t('web_common_is_required'),
    };

    return (
        <Spin spinning={initIng}>
            <Form
                form={form}
                onFinishFailed={onFinishFailed}
                onFinish={submitHandle}
                layout="vertical"
                // layout="horizontal"
                // size="large"
                className={cn(className, styles.form)}
                validateTrigger={isValidateAll ? void 0 : ['onBlur', 'onSubmit']}
                onValuesChange={onValuesChangeHandle}
                validateMessages={validateMessages}
                // 设置component="div" 可以使嵌套子表单控制台不报错<form不能嵌套form>
                // 但 htmlType="submit" 会提交到父表单上 onFinish
                // 所以提交按钮使用 htmlType="button"，使用onClick ()=> form.submit()
                component="div"
            >
                {/*经销商用户需要填写邮箱*/}
                {isDealer && !hideEmail && <FormEmail/>}

                <Form.Item noStyle name="id" hidden>
                    <Input disabled/>
                </Form.Item>

                <Form.Item name="countryId" hidden>
                    <Input disabled/>
                </Form.Item>

                <Form.Item name="provinceId" hidden>
                    <Input disabled/>
                </Form.Item>

                <Form.Item name="cityId" hidden>
                    <Input disabled/>
                </Form.Item>

                <Row gutter={20}>
                    {!!addressRules && addressRules.map((itemRules) => {
                        switch (itemRules.key) {
                            case 'countryText':
                                return (
                                    <ColItem itemRules={itemRules} key={itemRules.key}>
                                        <CountryFormItem
                                            isDisabledSelectCountry={isDisabledSelectCountry}
                                            onSelectCountryHandle={onSelectCountryHandle}
                                            countries={countries}
                                            itemRules={itemRules}
                                        />
                                    </ColItem>
                                );
                            case 'provinceText':
                                return (
                                    <ColItem itemRules={itemRules} key={itemRules.key}>
                                        <ProvinceFormItem
                                            onSelectProvinceHandle={onSelectProvinceHandle}
                                            currentCountryObj={currentCountryObj}
                                            itemRules={itemRules}
                                        />
                                    </ColItem>
                                );
                            case 'cityText':
                                return (
                                    <ColItem itemRules={itemRules} key={itemRules.key}>
                                        <CityFormItem
                                            currentCountryObj={currentCountryObj}
                                            addressRules={addressRules}
                                            cityList={cityList}
                                            cityLoading={cityLoading}
                                            onSelectCityHandle={onSelectCityHandle}
                                            itemRules={itemRules}
                                        />
                                    </ColItem>
                                );
                            case 'zipcode':
                                return (
                                    <ColItem itemRules={itemRules} key={itemRules.key}>
                                        <ZipcodeFormItem
                                            currentCountryObj={currentCountryObj}
                                            zipCodeList={zipCodeList}
                                            itemRules={itemRules}
                                        />
                                    </ColItem>
                                );
                            case 'mobile':
                                return (
                                    <ColItem itemRules={itemRules} key={itemRules.key}>
                                        <MobileFormItem currentCountryObj={currentCountryObj} itemRules={itemRules}/>
                                    </ColItem>
                                );
                            default:
                                return (
                                    <ColItem itemRules={itemRules} key={itemRules.key}>
                                        <FormInput itemRules={itemRules}/>
                                    </ColItem>
                                )
                        }
                    })}
                </Row>

                {showDefaultSwitch ? (
                    <Form.Item
                        className={cn(
                            styles['horizontal-style'],
                            styles['reverse'],
                        )}
                        name="isDefault"
                        label={t('web_address_set_as_default')}
                        valuePropName="checked"
                    >
                        <Switch className={switchStyles['big-style']}/>
                    </Form.Item>
                ) : (
                    <Form.Item noStyle name="isDefault" hidden>
                        <Input disabled/>
                    </Form.Item>
                )}

                {showShippingNote && (
                    <Form.Item>
                        <Alert
                            message={t('web_address_shipping_note')}
                            type="warning"
                            showIcon
                        />
                    </Form.Item>
                )}

                {showSubmit && (
                    <Form.Item className={'pt-2'}>
                        <Row gutter={20}>
                            {showCancelButton && (
                                <Col span={12}>
                                    <Button
                                        block
                                        onClick={() => onCancelHandle()}
                                    >
                                        {t('web_common_btn_cancel')}
                                    </Button>
                                </Col>
                            )}

                            <Col span={showCancelButton ? 12 : 24}>
                                <Button
                                    block
                                    disabled={isSubmitDisabled}
                                    type="primary"
                                    htmlType="button"
                                    onClick={async () => {
                                        form.submit()
                                    }}
                                    loading={loading}
                                >
                                    {t('web_common_btn_save')}
                                </Button>
                            </Col>
                        </Row>
                    </Form.Item>
                )}
            </Form>
        </Spin>
    )
};

export default AddressForm;